/** Store proof goes to our server before StoreKit is allowed to finish it. */
export interface StoreProduct { product_id: string; base_plan_id?: string; price: string }
export interface PurchaseRequest { product_id: string; base_plan_id?: string; customer_ref: string; trial_allowed?: boolean }
export type Receipt = { provider: "apple"; signed_transaction: string; transaction_id: string }
  | { provider: "google"; purchase_token: string; product_id: string };
export interface PurchaseResult { status: "purchased" | "pending" | "cancelled"; receipts: Receipt[] }
export interface PurchaseBridge {
  products(this: void, ids: readonly string[]): Promise<StoreProduct[]>;
  purchase(this: void, request: PurchaseRequest): Promise<PurchaseResult>;
  restore(this: void): Promise<PurchaseResult>;
  finish(this: void, receipt: Receipt): Promise<void>;
  onUpdate(this: void, listener: (receipt: Receipt) => void): () => void;
}
export type PurchaseState = "idle" | "purchasing" | "claiming" | "pending" | "cancelled" | "complete" | "error";
export class PurchaseBatchError extends Error {
  readonly failures: { receipt: Receipt; cause: unknown }[];
  constructor(failures: { receipt: Receipt; cause: unknown }[]) {
    super(`${failures.length} purchase receipts could not be verified. Other purchases were checked. ${failures.map(({ cause }) => cause instanceof Error ? cause.message : "Restore purchases to retry.").join(" ")}`);
    this.failures = failures;
  }
}
export class PurchaseFlow {
  state: PurchaseState = "idle";
  private bridge: PurchaseBridge;
  private claim: (receipt: Receipt) => Promise<void>;
  private refresh: () => Promise<unknown>;
  private changed: (state: PurchaseState) => void;
  constructor(bridge: PurchaseBridge, claim: (receipt: Receipt) => Promise<void>,
    refresh: () => Promise<unknown>, changed: (state: PurchaseState) => void = () => undefined) {
    this.bridge = bridge; this.claim = claim; this.refresh = refresh; this.changed = changed;
  }
  private set(state: PurchaseState): void { this.state = state; this.changed(state); }
  async accept(receipts: Receipt[]): Promise<void> {
    try {
      this.set("claiming");
      const failures: { receipt: Receipt; cause: unknown }[] = [];
      for (const receipt of receipts) {
        try { await this.claim(receipt); await this.bridge.finish(receipt); }
        catch (cause) { failures.push({ receipt, cause }); console.warn("Store receipt quarantined; continuing purchase checks:", cause); }
      }
      await this.refresh();
      if (failures.length > 0) throw new PurchaseBatchError(failures);
      this.set("complete");
    } catch (cause) { this.set("error"); throw cause; }
  }
  private async run(work: () => Promise<PurchaseResult>): Promise<void> {
    this.set("purchasing");
    try {
      const result = await work();
      if (result.status !== "purchased") { this.set(result.status); return; }
      await this.accept(result.receipts);
    } catch (cause) { this.set("error"); throw cause; }
  }
  buy(request: PurchaseRequest): Promise<void> { return this.run(() => this.bridge.purchase(request)); }
  restore(): Promise<void> { return this.run(() => this.bridge.restore()); }
}
export const webPurchases: PurchaseBridge = {
  products: () => Promise.resolve([]),
  purchase: () => Promise.reject(new Error("Web purchases use Paddle.")),
  restore: () => Promise.reject(new Error("Web subscriptions are restored by signing in.")),
  finish: () => Promise.resolve(), onUpdate: () => () => undefined,
};

function purchaseRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null) throw new Error("Malformed store response.");
  return value as Record<string, unknown>;
}
function purchaseString(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) throw new Error("Missing store purchase field.");
  return value;
}
export function parseReceipt(value: unknown): Receipt {
  const row = purchaseRecord(value);
  if (row.provider === "apple") return { provider: "apple", signed_transaction: purchaseString(row.signed_transaction), transaction_id: purchaseString(row.transaction_id) };
  if (row.provider === "google") return { provider: "google", purchase_token: purchaseString(row.purchase_token), product_id: purchaseString(row.product_id) };
  throw new Error("Unknown purchase provider.");
}
export function parsePurchaseResult(value: unknown): PurchaseResult {
  const row = purchaseRecord(value);
  if (row.status !== "purchased" && row.status !== "pending" && row.status !== "cancelled") throw new Error("Unknown store purchase state.");
  if (!Array.isArray(row.receipts)) throw new Error("Missing store receipts.");
  const receipts = row.receipts.map(parseReceipt);
  if (row.status !== "purchased" && receipts.length > 0) throw new Error("Pending or cancelled purchases cannot carry entitlement proof.");
  return { status: row.status, receipts };
}
export function parseStoreProducts(value: unknown): StoreProduct[] {
  const row = purchaseRecord(value);
  if (!Array.isArray(row.products)) throw new Error("Missing store products.");
  return row.products.map((value: unknown) => {
    const product = purchaseRecord(value);
    return { product_id: purchaseString(product.product_id), price: purchaseString(product.price),
      ...(product.base_plan_id === undefined ? {} : { base_plan_id: purchaseString(product.base_plan_id) }) };
  });
}
